import React, { useMemo } from 'react';
import {
  PLAYHEAD_WIDTH,
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HORIZONTAL_PADDING,
} from '../constants';
import { getItemLeftOffset } from '../utils/position-utils';
import { useTimelineHeight } from '../utils/use-context';
import { Z_INDEX_PLAYHEAD } from '../z-indices';

const CAP_PATH_D =
  'M4 1H8C9.65685 1 11 2.34315 11 4V20C11 21.6569 9.65685 23 8 23H4C2.34315 23 1 21.6569 1 20V4C1 2.34315 2.34315 1 4 1Z';

const INDICATOR_BAR_X = 5;
const INDICATOR_BAR_Y = 24;
const INDICATOR_BAR_WIDTH = 2;

const makeSkimmerPath = (height: number) => {
  return {
    capPath: CAP_PATH_D,
    barHeight: Math.max(height, 0),
  };
};

const SKIMMER_SVG_WIDTH = 12;
const SKIMMER_SVG_HEIGHT = 32;

interface SkimmerProps {
  skimFrame: number;
  visibleFrames: number;
  height: number;
  timelineWidth: number;
}

export const Skimmer: React.FC<SkimmerProps> = ({
  skimFrame,
  visibleFrames,
  height,
  timelineWidth,
}) => {
  const left =
    getItemLeftOffset({
      from: skimFrame,
      totalDurationInFrames: visibleFrames,
      timelineWidth,
    }) + TIMELINE_HORIZONTAL_PADDING;

  const style = useMemo(() => {
    return {
      left,
      marginLeft: -PLAYHEAD_WIDTH / 2,
    };
  }, [left]);

  const timelineHeight = useTimelineHeight();

  const { capPath, barHeight } = useMemo(() => {
    return makeSkimmerPath(
      Math.max(height, timelineHeight + TIMELINE_HEADER_HEIGHT) /
        (PLAYHEAD_WIDTH / SKIMMER_SVG_WIDTH),
    );
  }, [height, timelineHeight]);

  const svgStyle = useMemo(() => {
    return {
      width: PLAYHEAD_WIDTH,
      aspectRatio: `${SKIMMER_SVG_WIDTH} / ${SKIMMER_SVG_HEIGHT}`,
      overflow: 'visible',
      marginTop: 0,
    };
  }, []);

  const stickyStyle: React.CSSProperties = useMemo(() => {
    return {
      position: 'sticky',
      zIndex: Z_INDEX_PLAYHEAD - 1,
      top: 0,
    };
  }, []);

  return (
    <div
      className="pointer-events-none absolute top-0 flex h-full flex-col items-center"
      style={style}
      id="skimmer"
    >
      <div style={stickyStyle}>
        <svg
          style={svgStyle}
          viewBox={`0 0 ${SKIMMER_SVG_WIDTH} ${SKIMMER_SVG_HEIGHT}`}
          fill="none"
        >
          <path
            d={capPath}
            fill="var(--comp-label-yellow-bg)"
            stroke="var(--comp-label-yellow-bg)"
            strokeWidth={2}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <rect
            x={INDICATOR_BAR_X}
            y={INDICATOR_BAR_Y}
            width={INDICATOR_BAR_WIDTH}
            height={barHeight}
            fill="var(--comp-label-yellow-bg)"
          />
        </svg>
      </div>
    </div>
  );
};
